"use client";

import { useCallback } from "react";
import { useWalletActionGuard } from "./useWalletActionGuard";
import { useSignTypedData } from "wagmi";

type SignTypedDataParams = Parameters<ReturnType<typeof useSignTypedData>["signTypedDataAsync"]>[0];

/**
 * Wrapper hook for wagmi's useSignTypedData that ensures the wallet is connected before signing.
 *
 * Scenario: User is authenticated with Privy but their wallet got disconnected
 * (e.g., MetaMask disconnected, mobile in-app browser session dropped after phone sleep).
 * This hook will attempt to reconnect the user's previous wallet connector before signing,
 * and retry once if the connector is stale despite appearing connected.
 *
 * Uses wagmi's useReconnect (connector-agnostic) instead of hardcoding injected(),
 * so it works with MetaMask, WalletConnect, Rabby, and any other connector type.
 *
 * @returns {Object} Hook return value
 * @returns {Function} signTypedDataAsync - Signs typed data, reconnecting wallet if needed.
 * @returns {boolean} isPending - Whether a signing operation is in progress
 */
export const useSignTypedDataWithConnection = () => {
  const { executeMainEvmAction } = useWalletActionGuard();
  const { signTypedDataAsync: wagmiSignTypedDataAsync, isPending } = useSignTypedData();

  const signTypedDataAsync = useCallback(
    async (params: SignTypedDataParams): Promise<`0x${string}`> => {
      return executeMainEvmAction(() => wagmiSignTypedDataAsync(params), { retryOnConnectorError: true });
    },
    [executeMainEvmAction, wagmiSignTypedDataAsync],
  );

  return {
    signTypedDataAsync,
    isPending,
  };
};
